import { InteractionType } from "../models/interactions/Interaction";
import { ISequence } from "../models/interactions/SequenceModel";
// Type-only: loading the Trigger model pulls in its node preview components.
import type { ITrigger, TriggerType } from "../models/interactions/Trigger";

export const ON_TAKE_START = "onStart" as TriggerType;
export const ON_TAKE_END = "onEnd" as TriggerType;

/**
 * The enabled nodes a take trigger (On Take Start, On Take End) plays: every
 * node reachable from the trigger node, without the trigger itself.
 */
export function getTriggerChainNodeIds(
  sequence: ISequence | undefined,
  triggerType: TriggerType,
): string[] {
  const nodes = sequence?.nodes ?? [];
  const trigger = nodes.find(
    (node) =>
      node.data?.interaction?.type === InteractionType.Trigger &&
      (node.data.interaction as ITrigger).triggerType === triggerType,
  );
  if (!trigger) return [];

  const outgoing = new Map<string, string[]>();
  (sequence?.edges ?? []).forEach((edge) =>
    outgoing.set(edge.source, [...(outgoing.get(edge.source) ?? []), edge.target]),
  );
  const seen = new Set<string>([trigger.id]);
  const queue = [...(outgoing.get(trigger.id) ?? [])];
  while (queue.length > 0) {
    const nodeId = queue.shift()!;
    if (seen.has(nodeId)) continue;
    seen.add(nodeId);
    queue.push(...(outgoing.get(nodeId) ?? []));
  }
  seen.delete(trigger.id);

  const enabled = new Set(
    nodes.filter((node) => !node.data?.interaction?.disabled).map((node) => node.id),
  );
  return [...seen].filter((nodeId) => enabled.has(nodeId));
}
